import { useDataCatalogNavigation } from '@/app/data-catalog/_common/hooks/use-data-catalog-breadcrumbs'
import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { useDataQualScoreCardsQuery } from '@/hooks/api/use-datagovernance-dashboard-query'
import { usePersistentSorting } from '@/hooks/use-persistent-sorting'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  type ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import { Info } from 'lucide-react'
import React from 'react'
import { FiltersType } from '../..'
import { getStyles } from '../../utils'
import { AssessmentDetails } from '../_common/assessment-details'
import { DimensionColumnFilter } from '../_common/dimension-filter'
import { ScoreCardsUpdate } from '../_common/scorecards-update'

type DataQualityMetricsTableDef = {
  metric: string
  score: number
  dimension: string
  description: string
}

const multiValueFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId)
  if (!filterValue || filterValue.length === 0) return true
  return filterValue.includes(cellValue)
}

export function DataQualityView({ filters }: { filters: FiltersType }) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = usePersistentSorting({
    key: 'dg-dashboard-dataquality',
  })
  const { openIndustryReferenceTab } = useDataCatalogNavigation()

  const memoizedFilters = React.useMemo(() => filters, [JSON.stringify(filters)])

  const dataQualityScoreCardQuery = useDataQualScoreCardsQuery({
    filters: memoizedFilters,
    queryOptions: {
      enabled: !!memoizedFilters,
    },
  })

  const { metrics_scope, overall_assessment } = dataQualityScoreCardQuery?.data || {}

  const scoreCards =
    overall_assessment?.dimensions.map((dqScore) => {
      return {
        title: dqScore.dimension_name,
        dimension_description: {
          name: dqScore.dimension_name,
          description: dqScore.description,
          purpose: '',
        },
        total: dqScore.max_score,
        passed: dqScore.score,
        score: dqScore.percentage_score,
      }
    }) || []

  const dimensionFilterOptions = React.useMemo(() => {
    return (
      overall_assessment?.dimensions.map((dim) => ({
        label: dim.dimension_name,
        value: dim.dimension_name,
      })) || []
    )
  }, [overall_assessment])

  const dataQualityMetricsColumnDef: ColumnDef<DataQualityMetricsTableDef>[] = React.useMemo(
    () => [
      {
        enableColumnFilter: false,
        enableSorting: false,
        accessorKey: 'metric',
        size: 50,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='dataGovernanceDashboard.metricsTable.headers.metric'
          >
            <T keyName='dataGovernanceDashboard.metricsTable.headers.metric' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string | null>()
          return <p className='text-sm font-medium'>{value}</p>
        },
      },
      {
        enableColumnFilter: false,
        accessorKey: 'score',
        size: 50,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='dataGovernanceDashboard.metricsTable.headers.score'
            columnSort={{
              tooltip: {
                default: 'dataGovernanceDashboard.metricsTable.sort.tooltip.score.default',
                desc: 'dataGovernanceDashboard.metricsTable.sort.tooltip.score.desc',
              },
            }}
          >
            <T keyName='dataGovernanceDashboard.metricsTable.headers.score' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string | null>()
          return (
            <p className={cn('text-sm font-semibold', getStyles(value ? +value : 0).text)}>
              {value ? `${value}%` : 'NA'}
            </p>
          )
        },
      },
      {
        size: 50,
        accessorKey: 'dimension',
        filterFn: multiValueFilter,
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='dataGovernanceDashboard.metricsTable.headers.dimension'
            FilterComp={(props) => (
              <DimensionColumnFilter {...props} filterOptions={dimensionFilterOptions} />
            )}
          >
            <T keyName='dataGovernanceDashboard.metricsTable.headers.dimension' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string | null>()
          return <p className={`text-sm font-normal`}>{value}</p>
        },
      },
      {
        enableColumnFilter: false,
        accessorKey: 'description',
        enableSorting: false,
        size: 400,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='dataGovernanceDashboard.metricsTable.headers.description'
          >
            <T keyName='dataGovernanceDashboard.metricsTable.headers.description' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string | null>()
          return <p className='text-sm font-normal'>{value}</p>
        },
      },
    ],
    [dimensionFilterOptions],
  )

  const tableData = React.useMemo(
    () =>
      overall_assessment?.dimensions.flatMap((dimension) =>
        dimension.metrics.map((metric) => ({
          metric: metric.metric_name,
          description: metric.description,
          score: metric.percentage_score,
          dimension: metric.dimension,
        })),
      ) || [],
    [overall_assessment],
  )

  const table = useReactTable<DataQualityMetricsTableDef>({
    data: tableData,
    columns: dataQualityMetricsColumnDef,
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      columnFilters,
      sorting,
    },
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = dataQualityScoreCardQuery.status === 'pending'
  const isTableLoading =
    isInitialLoading && dataQualityScoreCardQuery.isFetching && rows.length === 0

  return (
    <>
      <AssessmentDetails
        metaData={metrics_scope}
        totalIssues={overall_assessment?.total_issues || 0}
        issuePercentage={overall_assessment?.percentage_score || 0}
        scoreCards={scoreCards}
        isLoading={
          dataQualityScoreCardQuery.status === 'pending' &&
          dataQualityScoreCardQuery.isFetching &&
          rows.length === 0
        }
        view='dataQuality'
        error={dataQualityScoreCardQuery.error}
      />
      {dataQualityScoreCardQuery.isFetching && (
        <ScoreCardsUpdate message='Updated Data Quality Scores' />
      )}
      <div className='my-6'>
        <h2 className='py-3 font-semibold text-gray-900'>Data Quality Assessment Metrics</h2>
        <div className='flex items-center justify-between gap-2'>
          <p className='text-sm text-gray-600'>{`Showing ${tableData.length} metrics`}</p>
          <Button
            unstyled
            className='flex items-center gap-1 text-sm font-light hover:text-neutral-300'
            onClick={openIndustryReferenceTab}
          >
            <Info className='size-4' />
            Industry Reference
          </Button>
        </div>
        <div className='mt-8'>
          <Table>
            <TableHeader className={cn('bg-content z-10 bg-neutral-25')}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ width: `${header.getSize()}px` }}
                      className={cn(header.id === 'options' && 'text-center')}
                      data-state={
                        (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isTableLoading && (
                <TableSkeletonRows
                  cellCount={dataQualityMetricsColumnDef.length}
                  className='bg-neutral-white'
                />
              )}
              {dataQualityScoreCardQuery.status === 'error' && (
                <TableErrorRow colSpan={dataQualityMetricsColumnDef.length} />
              )}
              {dataQualityScoreCardQuery.status === 'success' &&
                rows.length > 0 &&
                rows.map((row) => (
                  <TableRow key={row.id} className='h-[72px] bg-neutral-white'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        data-state={
                          (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {dataQualityScoreCardQuery.status === 'success' &&
                rows?.length === 0 &&
                !dataQualityScoreCardQuery.isFetching && (
                  <TableRow>
                    <TableCell
                      colSpan={dataQualityMetricsColumnDef.length}
                      className='h-24 text-center'
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}
